import React, { useState } from 'react';
import {
	BringzzButton, BringzzText, BringzzPageContainer,
	BringzzInput, BringzzIcon, BringzzCountryDropdown
} from "@bringzz/components";
import { useNavigation } from 'context/NavigationContext';
import useRequest from 'hooks/useRequest';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AnimatedPlaceholderInput from 'components/AnimatedPlaceholderInput';
import '../auth.css';

const RegisterPage = () => {
	const { backPress, navigate } = useNavigation();
	const { sendRequest, loading } = useRequest();
	const { t } = useTranslation();

	const [country, setCountry] = useState({
		name: 'Austria',
		dial_code: '+43',
		code: 'AT'
	});

	const [identifierValue, setIdentifierValue] = useState('');
	const [isValidIdentifier, setIsValidIdentifier] = useState(false);
	const [password, setPassword] = useState('');
	const [isValidPassword, setIsValidPassword] = useState(false);
	const [show, setShow] = useState(false);
	const [passwordTouched, setPasswordTouched] = useState(false);
	const [passwordFocused, setPasswordFocused] = useState(false);

	const phoneRegex = /^\d{5,}$/;
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	const getMethod = () => {
		if (phoneRegex.test(identifierValue)) return 'phone';
		if (emailRegex.test(identifierValue)) return 'email';
		return null;
	};

	const getLoginValue = () => {
		const method = getMethod();
		if (method === 'phone') return `${country.dial_code} ${identifierValue}`;
		return identifierValue;
	};

	const isFormValid = isValidIdentifier && isValidPassword;

	const preUserCreation = async () => {
		const method = getMethod();
		const loginValue = getLoginValue();

		const result = await sendRequest({
			method: 'POST',
			url: `/module/user/preUserCreation`,
			data: {
				login: loginValue,
				password: password,
				logintype: method === 'phone' ? 1 : 0,
				language: "en"
			}
		}).catch(error => {
			console.error(error);
			return error;
		});

		if (result.status === "success") {
			const userId = result.data.preuser;
			const encodedValue = method === 'phone'
				? `${country.dial_code}%20${identifierValue}`
				: identifierValue;
			toast.success(t('component.auth.register.success.verification_sent'));
			navigate(
				`/register/otp?method=${method}&userId=${userId}&value=${encodedValue}&redirect=/register/consumer/profile`, false
			);
		} else {
			const errorMessage = result.message || 'Registration failed';
			if (errorMessage.includes('(0x5)')) {
				toast.error(t('component.auth.register.errors.already_exists_ongoing'));
			} else if (errorMessage.includes('(0x4)')) {
				toast.error(t('component.auth.register.errors.already_exists_sign_in'));
			} else {
				toast.error(errorMessage);
			}
		}
	};

	const getPasswordValidationState = () => {
		if (passwordTouched && !passwordFocused) {
			if (password === '') return 'error';
			if (password && !isValidPassword) return 'error';
			if (isValidPassword) return 'success';
		}
		return null;
	};

	const getPasswordMessage = () => {
		if (passwordTouched && !passwordFocused) {
			if (password === '') return t('component.auth.register.password.required');
			if (password && !isValidPassword) return t('component.auth.register.password.does_not_match');
			if (isValidPassword) return t('component.auth.register.password.check');
		}
		return null;
	};

	const getPasswordHintMessage = () => {
		if (passwordFocused || (!passwordTouched || password === '')) {
			return t('component.auth.register.password.hint');
		}
		return null;
	};

	return (
		<BringzzPageContainer
			className='bg-white auth-no-scroll'
			header={{
				left: (
					<BringzzIcon
						size='22'
						icon='IconChevronLeft'
						folder='LineIcons'
						className='cursor-pointer'
						onClick={backPress}
					/>
				),
				title: t('component.auth.register.title')
			}}
			authenticated={false}
			footer={
				<div className='pb-20 px-6'>
					<BringzzText tag="h5" className="py-2 font-normal">
						{t('component.auth.register.agreement.text')}{' '}
						<span className='font-bold'>{t('component.auth.register.agreement.privacy_policy')}</span>{' '}
						{t('component.auth.register.agreement.and')}{' '}
						<span className='font-bold'>{t('component.auth.register.agreement.terms_of_use')}</span>.
					</BringzzText>
					<BringzzButton
						size='lg'
						className='bg-magic-lilac w-full flex justify-center text-midnight-blue'
						onClick={preUserCreation}
						disabled={!isFormValid || loading}
					>
						{t('component.auth.register.buttons.create_account')}
					</BringzzButton>
				</div>
			}
		>
			<div className='relative flex justify-center bg-magic-lilac/20'>
				<div className='relative bg-white w-full bg-cover bg-center flex flex-col md:max-w-2xl'>
					<div className='flex-grow flex flex-col justify-between py-12 px-4'>
						<div className='z-10 flex flex-col'>
							<div className='flex flex-col space-y-6 w-full'>
								<BringzzText tag='h1' className='pl-2 font-bold text-midnight-blue'>
									{t('component.auth.register.welcome')}
								</BringzzText>
								<BringzzText tag='h3' className='font-normal text-midnight-blue'>
									{t('component.auth.register.create_account_intro')}
								</BringzzText>
							</div>
							<div className='mt-8'>
								<AnimatedPlaceholderInput
									containerClassname="w-full"
									className="!pl-16"
									type='text'
									placeholders={[t('component.auth.input.phone'), t('component.auth.input.email')]}
									value={identifierValue}
									onChange={(e, isValid, newValue) => {
										setIsValidIdentifier(isValid);
										setIdentifierValue(newValue);
									}}
									pattern='^(?:\+?\d{1,3}[-\s.]?\d{3,}|\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+)$'
									icon={{
										left: <BringzzCountryDropdown
											className="!rounded-r-none"
											mode={'modal'}
											onCountryChange={result => {
												setCountry(result);
											}}
										/>
									}}
								/>
							</div>
							<div className='mt-6'>
								<BringzzInput
									type={show ? 'text' : 'password'}
									hint={getPasswordHintMessage()}
									label={t('component.auth.register.password.label')}
									pattern='^(?=.*[A-Za-z])(?=.*\d)(?=.*[!?@#$%^&*()-_+=])[A-Za-z\d!?@#$%^&*()-_+=]{9,}$'
									validationState={getPasswordValidationState()}
									customMessage={getPasswordMessage()}
									icon={{
										right: (
											<BringzzIcon
												icon={show ? 'IconEye' : 'IconEyeSlash'}
												folder='LineIcons'
												className='cursor-pointer'
												size='16'
												onClick={() => setShow(!show)}
											/>
										)
									}}
									onChange={(e, isValid, newValue) => {
										setIsValidPassword(isValid);
										setPassword(newValue);
										setPasswordTouched(true);
									}}
									onBlur={() => {
										setPasswordFocused(false);
										setPasswordTouched(true);
									}}
									inputProps={{
										onFocus: () => setPasswordFocused(true)
									}}
								/>
							</div>
							<div className='flex items-center gap-x-2 mt-4'>
								<BringzzText tag='h4' className='font-normal text-midnight-blue'>
									{t('component.auth.register.already_have_account')}
								</BringzzText>
								<BringzzText
									tag='h4'
									className='text-midnight-blue underline cursor-pointer font-medium'
									onClick={() => navigate('/login')}
								>
									{t('component.auth.register.sign_in_link')}
								</BringzzText>
							</div>
						</div>
					</div>
				</div>
			</div>
		</BringzzPageContainer>
	);
};

export default RegisterPage;
